import { ApiError, apiRequest } from "./apiClient";
import type {
  ExtractFieldsResult,
  GameRuleHealthStatus,
  KbSectionSavePayload,
  KbSectionSaveResult,
  KbTagRow,
} from "../types/gameRuleKb";
import type { ExtractFieldsApiField } from "../types/gameRuleKb";

type SuccessResponse<T> = {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const parsed = JSON.parse(raw) as { detail?: string; message?: string };
    if (typeof parsed.detail === "string") {
      return parsed.detail;
    }
    if (typeof parsed.message === "string") {
      return parsed.message;
    }
    return raw || `HTTP ${response.status}`;
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

export const getGameRuleHealth = async (): Promise<GameRuleHealthStatus> => {
  const response = await apiRequest("/game-rule/health", undefined, { requiresAuth: false });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<GameRuleHealthStatus>;
  return json.data;
};

export const listKbTags = async (q?: string): Promise<KbTagRow[]> => {
  const query = (q ?? "").trim();
  const path = query
    ? `/game-rule/kb/tags?q=${encodeURIComponent(query)}`
    : "/game-rule/kb/tags";
  const response = await apiRequest(path, undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbTagRow[]>;
  return Array.isArray(json.data) ? json.data : [];
};

export const extractKbFields = async (
  contentPlain: string,
  sectionName: string,
  contentStructure?: Record<string, unknown> | null,
): Promise<ExtractFieldsResult> => {
  const response = await apiRequest("/game-rule/kb/extract-fields", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      content_plain: contentPlain.trim(),
      section_name: sectionName.trim(),
      content_structure: contentStructure ?? undefined,
    }),
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<{
    fields?: ExtractFieldsApiField[];
    extracted_content?: string | null;
    extracted_structure?: Record<string, unknown> | null;
    content_structure?: Record<string, unknown> | null;
  }>;
  return {
    fields: Array.isArray(json.data?.fields) ? json.data.fields : [],
    extractedContent: json.data?.extracted_content ?? null,
    extractedStructure: json.data?.extracted_structure ?? null,
    contentStructure: json.data?.content_structure ?? null,
  };
};

export const saveKbSection = async (payload: KbSectionSavePayload): Promise<KbSectionSaveResult> => {
  const response = await apiRequest("/game-rule/kb/sections", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbSectionSaveResult>;
  return json.data;
};
